// achievements.js
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { calculateRankProgression } from './calculateRankProgression';

export const ACHIEVEMENTS = {
    CONVERSATION_MILESTONES: {
      FIRST_CONVO: { id: 'first_convo', name: 'Opening Kickoff', description: 'Start your first betting conversation', points: 20, threshold: 1 },
      CHAT_MASTER: { id: 'chat_master', name: 'Season Ticket Holder', description: 'Start 50 conversations with the AI', points: 200, threshold: 50 },
      SOCIALITE: { id: 'socialite', name: 'High Roller', description: 'Start 200 conversations about your picks', points: 500, threshold: 200 }
    },
    MESSAGE_MILESTONES: {
      FIRST_MESSAGE: { id: 'first_message', name: 'First Down', description: 'Send your first message to the AI', points: 20, threshold: 1 },
      CHATTY: { id: 'chatty', name: 'Play Caller', description: 'Send 100 messages discussing picks and odds', points: 100, threshold: 100 },
      WORDSMITH: { id: 'wordsmith', name: 'Sports Analyst', description: 'Send 1,000 messages analyzing betting opportunities', points: 500, threshold: 1000 }
    },
    THUMBS_UP_MILESTONES: {
      FIRST_THUMBS_UP: { id: 'first_thumbs_up', name: 'Good Pick', description: 'Receive your first thumbs up on betting advice', points: 25, threshold: 1 },
      CROWD_PLEASER: { id: 'crowd_pleaser', name: 'Hot Streak', description: 'Receive 50 thumbs up on your betting discussions', points: 250, threshold: 50 },
      CELEBRITY: { id: 'celebrity', name: 'Betting Legend', description: 'Receive 500 thumbs up from the community', points: 1000, threshold: 500 }
    },
    STREAK_MILESTONES: {
      WEEKEND_WARRIOR: { id: 'weekend_warrior', name: 'Weekend Warrior', description: 'Use the platform on 3 consecutive weekends', points: 75, threshold: 3 },
      DAILY_BETTOR: { id: 'daily_bettor', name: 'Daily Bettor', description: 'Check odds and lines for 7 days straight', points: 150, threshold: 7 },
      BETTING_PRO: { id: 'betting_pro', name: 'Betting Professional', description: 'Analyze picks for 30 consecutive days', points: 500, threshold: 30 }
    },
    SPECIAL_ACHIEVEMENTS: {
      PARLAY_MASTER: { id: 'parlay_master', name: 'Parlay Master', description: 'Add 5 different picks to your bet slip in one session', points: 100, threshold: 5 },
      NIGHT_OWL: { id: 'night_owl', name: 'Night Owl', description: 'Use the platform during late-night hours 10 times', points: 150, threshold: 10 },
      UNDERDOG_FAN: { id: 'underdog_fan', name: 'Underdog Fan', description: 'Discuss underdogs with positive odds in 20 conversations', points: 200, threshold: 20 }
    }
  };
  
  export const checkAndUpdateAchievements = async (userId, achievementType, currentCount) => {
    const userStatsRef = doc(db, "userStats", userId);
    
    try {
      const userStatsDoc = await getDoc(userStatsRef);
      if (!userStatsDoc.exists()) return null;
      
      const data = userStatsDoc.data();
      const unlockedAchievements = data.achievements.unlocked || {};
      let bonusPoints = 0;
      const newAchievements = [];
      
      // Check if achievementType exists in ACHIEVEMENTS
      if (!ACHIEVEMENTS[achievementType]) {
        console.error(`Achievement type ${achievementType} not found`);
        return null;
      }
      
      // Find all achievements that should be unlocked based on the current count
      Object.values(ACHIEVEMENTS[achievementType]).forEach(achievement => {
        // Skip if already unlocked
        if (unlockedAchievements[achievement.id]) return;
        
        if (currentCount >= achievement.threshold) {
          unlockedAchievements[achievement.id] = {
            unlockedAt: new Date().toISOString(),
            name: achievement.name,
            description: achievement.description,
            points: achievement.points
          };
          bonusPoints += achievement.points;
          newAchievements.push(achievement);
        }
      });
      
      if (newAchievements.length > 0) {
        const totalPoints = (data.points.total || 0) + bonusPoints;
        
        await updateDoc(userStatsRef, {
          "achievements.unlocked": unlockedAchievements,
          "points.total": totalPoints
        });
        
        // Recalculate rank after points update
        await calculateRankProgression(userId);
        
        return {
          newAchievements,
          bonusPoints
        };
      }
      
      return null;
    } catch (error) {
      console.error("Error checking achievements:", error);
      return null;
    }
  };

// Track parlay additions
export const trackParlayAdd = async (userId, pickCount) => {
  const userStatsRef = doc(db, "userStats", userId);
  
  try {
    const userStatsDoc = await getDoc(userStatsRef);
    if (!userStatsDoc.exists()) return;
    
    const data = userStatsDoc.data();
    
    // Update the parlay count
    await updateDoc(userStatsRef, {
      "achievements.parlayAdded": (data.achievements.parlayAdded || 0) + pickCount
    });
    
    // Check for Parlay Master achievement
    if (pickCount >= 5) {
      await checkAndUpdateAchievements(userId, 'SPECIAL_ACHIEVEMENTS', 5);
    }
  } catch (error) {
    console.error("Error tracking parlay add:", error);
  }
};

// Track night owl sessions
export const trackNightOwlSession = async (userId) => {
  const now = new Date();
  const hour = now.getHours();
  
  // Only count as night owl if it's between 10PM and 2AM
  if (hour >= 22 || hour < 2) {
    const userStatsRef = doc(db, "userStats", userId);
    
    try {
      const userStatsDoc = await getDoc(userStatsRef);
      if (!userStatsDoc.exists()) return;
      
      const data = userStatsDoc.data();
      const newCount = (data.achievements.nightOwlSessions || 0) + 1;
      
      await updateDoc(userStatsRef, {
        "achievements.nightOwlSessions": newCount
      });
      
      // Check for Night Owl achievement
      await checkAndUpdateAchievements(userId, 'SPECIAL_ACHIEVEMENTS', newCount);
    } catch (error) {
      console.error("Error tracking night owl session:", error);
    }
  }
};

// Track underdog discussions
export const trackUnderdogDiscussion = async (userId) => {
  const userStatsRef = doc(db, "userStats", userId);
  
  try {
    const userStatsDoc = await getDoc(userStatsRef);
    if (!userStatsDoc.exists()) return;
    
    const data = userStatsDoc.data();
    const newCount = (data.achievements.underdogDiscussions || 0) + 1;
    
    await updateDoc(userStatsRef, {
      "achievements.underdogDiscussions": newCount
    });
    
    // Check for Underdog Fan achievement
    await checkAndUpdateAchievements(userId, 'SPECIAL_ACHIEVEMENTS', newCount);
  } catch (error) {
    console.error("Error tracking underdog discussion:", error);
  }
};